// @ts-check
/* eslint-disable camelcase */
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useGetters } from 'vuex-composition-helpers/dist';

import simpleHash from '@/utils/simpleHash';

/**
 * BulkSends creation steps routes
 *
 * provides step routes and current step index,
 * calculates previous and next routes for "steps" pages,
 * cancel and update routes for "edit" pages
 * progress route for resuming a draft
 */
export default function useBulkSendRoutes() {
  const router = useRouter();
  const currentRoute = useRoute();
  /** @type { import('@/store/modules/bulkSend').Getters} */
  const { bulkSend, stepsProgress, isSent } = useGetters({
    bulkSend: 'bulkSend/bulkSend',
    stepsProgress: 'bulkSend/stepsProgress',
    isSent: 'bulkSend/isSent',
  });

  const parentRoute = computed(() => currentRoute.matched[currentRoute.matched.length - 2]);
  const isEditStepRoute = computed(() => currentRoute.params.isEditStep);
  const isStepsRoute = computed(() =>
      (currentRoute && currentRoute.name === 'BulkSendsSteps') ||
      (parentRoute.value && parentRoute.value.name === 'BulkSendsSteps'));
  const isEditRoute = computed(() => parentRoute.value && parentRoute.value.name === 'BulkSendsEdit');
  const isCreateRoute = computed(() => currentRoute.name === 'BulkSendsCreate');

  const stepsRoutes =
    router.options.routes.find(route => route.name === 'BulkSendsSteps')
      .children || [];

  const stepsHash = computed(() =>
    simpleHash(stepsRoutes.map(route => route.name).join()));

  const templatesIndex = stepsRoutes.findIndex(route => route.name === 'BulkSendsStepTemplates');
  const rolesIndex = stepsRoutes.findIndex(route => route.name === 'BulkSendsStepRoles');
  const importIndex = stepsRoutes.findIndex(route => route.name === 'BulkSendsStepImportData');
  const summaryIndex = stepsRoutes.findIndex(route => route.name === 'BulkSendsStepSummary');

  const currentIndex = computed(() =>
    stepsRoutes.findIndex(route => route.name === currentRoute.name));

  /**
   * Progress update to POST
   *
   * we update progress when furthest step so far
   * or in template/roles/import flow
   * or steps have changed
   */
  const progressUpdate = computed(() => {
    if (
      currentIndex.value > (stepsProgress.value?.index || 0) ||
      currentIndex.value >= templatesIndex ||
      stepsHash.value !== stepsProgress.value?.hash
    ) {
      return { hash: stepsHash.value, index: currentIndex.value };
    }

    return undefined;
  });

  /**
   * Progress route
   *
   * redirect to this route to ensure all steps are progressed through in order
   */
  const progressRoute = computed(() => {
    const { hash, index } = stepsProgress.value || {};
    const progressIndex = index + 1;

    // no step in url go to next incomplete step
    // step in url is before go to it instead
    const nextIndex =
      currentIndex.value < 0
        ? progressIndex
        : Math.min(currentIndex.value, progressIndex);

    // bulk send has been sent so summary only
    if (isSent.value) {
      return stepsRoutes[summaryIndex];
    }

    // hashes don't match, steps have changed so go to beginning
    if (hash !== stepsHash.value) {
      return stepsRoutes[1];
    }

    // requires specific step
    if (
      bulkSend.value.import_status === 'requires-templates' &&
      nextIndex > templatesIndex
    ) {
      return stepsRoutes[templatesIndex];
    }
    if (
      bulkSend.value.import_status === 'requires-roles' &&
      nextIndex > rolesIndex
    ) {
      return stepsRoutes[rolesIndex];
    }
    if (
      bulkSend.value.import_status === 'requires-csv' &&
      nextIndex > importIndex
    ) {
      return stepsRoutes[importIndex];
    }

    // allow edit routes after reaching summary
    if (isEditRoute.value && progressIndex >= summaryIndex) {
      return currentRoute;
    }

    return stepsRoutes[nextIndex];
  });

  const reimportRoutes = {
    'requires-templates': stepsRoutes[templatesIndex],
    'requires-roles': stepsRoutes[rolesIndex],
    'requires-csv': stepsRoutes[importIndex],
  };

  const prevRoute = computed(() => {
    if (isEditRoute.value || isEditStepRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value - 1];
      return route && { name: route.name };
    }

    return undefined;
  });

  const nextRoute = computed(() => {
    if (isCreateRoute.value) {
      return {
        name: stepsRoutes[1].name,
        params: { fingerprint: 'fingerprint' },
      };
    }

    if (isEditRoute.value) {
      return stepsRoutes[summaryIndex];
    }

    if (isStepsRoute.value) {
      const route = stepsRoutes[currentIndex.value + 1];
      return route && { name: route.name };
    }

    return undefined;
  });

  return {
    prevRoute,
    nextRoute,
    progressRoute,
    reimportRoutes,
    stepsRoutes,
    currentIndex,
    progressUpdate,
    isStepsRoute,
    isEditRoute,
    isCreateRoute,
    isEditStepRoute,
  };
}
